import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast, Button, Spinner, Modal, Icon, WizardSteps } from '../../components/ds';
import api from '../../api/client';
import TargetingForm from '../../components/TargetingForm/TargetingForm';
import {
  makeDefaultState,
  parseDimensionFromAdGroup,
  validateDeviceOsStates,
  buildDimensionPayload,
} from '../../utils/deviceOsTargeting';
import {
  makeDefaultAgeGenderState,
  parseAgeFromAdGroup,
  parseGenderFromAdGroup,
  validateAgeGenderStates,
} from '../../utils/ageGenderTargeting';
import './CreateAdWizard.css';

const getDatePart = (dt) => dt.split('T')[0] || '';
const getTimePart = (dt, fallback = '00:00') => dt.split('T')[1] || fallback;
const setDatePart = (dt, date, timeFallback = '00:00') => `${date}T${getTimePart(dt, timeFallback)}`;
const setTimePart = (dt, time) => `${dt.split('T')[0] || new Date().toISOString().split('T')[0]}T${time}`;

const handleNumericInput = (setter) => (e) => {
  const raw = e.target.value.replace(/,/g, '');
  if (raw === '' || /^\d+$/.test(raw)) setter(raw);
};
const formatNumber = (val) => (val ? Number(val).toLocaleString() : '');

const EMPTY_DAY_PARTS = { mon: [], tue: [], wed: [], thu: [], fri: [], sat: [], sun: [] };


export default function CreateAdWizard() {
  const navigate = useNavigate();
  const { addToast } = useToast();
  const [searchParams] = useSearchParams();

  // Resume mode: read from URL query params
  const editId = searchParams.get('edit');
  const resumeStep = searchParams.get('step');
  const isResumeMode = !!editId;

  // Date validation helpers
  // todayStr: YYYY-MM-DD in local timezone (date-level, no time component)
  const todayStr = new Date().toLocaleDateString('sv'); // 'sv' locale gives YYYY-MM-DD

  // Resume mode: ad status flags (set once on load, never mutated by user input)
  const [adStatus, setAdStatus] = useState('');
  // initialStartTime / initialEndTime: snapshot from DB on load; used for adHasStarted / isEndExpired.
  // Must be captured once — never updated when user edits the form.
  // We use refs so they never trigger re-renders.
  const initialStartTimeRef = React.useRef(null);
  const initialEndTimeRef = React.useRef(null);

  // Lifecycle flags — computed once from DB snapshot (initialStartTimeRef / initialEndTimeRef).
  // Uses timestamp-level comparison (Date instants, timezone-agnostic via .getTime()).
  // paused is NOT a lock condition; archived locks all fields.
  // See AS-100 for the full policy spec.

  // isArchived: archived ads are fully locked
  const isArchived = adStatus === 'archived';

  // isStarted: ad has already begun (now >= dbStart); draft/creating are never started
  const isStarted = React.useMemo(() => {
    if (!isResumeMode) return false;
    if (!adStatus || adStatus === 'draft' || adStatus === 'creating') return false;
    const dbStart = initialStartTimeRef.current ? new Date(initialStartTimeRef.current) : null;
    if (!dbStart) return false;
    return new Date() >= dbStart;
  }, [isResumeMode, adStatus]); // eslint-disable-line

  // isEnded: ad end_time has passed
  // adStatus is included in deps so this memo re-runs after the init effect sets
  // initialEndTimeRef.current and calls setAdStatus — same pattern as isStarted (:65).
  // Without adStatus, the memo fires on mount before the ref is populated and stays false.
  const isEnded = React.useMemo(() => {
    if (!isResumeMode) return false;
    const dbEnd = initialEndTimeRef.current ? new Date(initialEndTimeRef.current) : null;
    if (!dbEnd) return false;
    return new Date() > dbEnd;
  }, [isResumeMode, adStatus]); // eslint-disable-line

  // isEndExpired: ended more than 3 days (72 h) ago — locks end_time and impressions.
  // Changed from 7-day to 3-day window per AS-100 design decision.
  // adStatus dep mirrors isEnded above — ensures re-eval after init effect populates the ref.
  const isEndExpired = React.useMemo(() => {
    if (!isResumeMode) return false;
    const dbEnd = initialEndTimeRef.current ? new Date(initialEndTimeRef.current) : null;
    if (!dbEnd) return false;
    return (new Date() - dbEnd) > 3 * 24 * 60 * 60 * 1000;
  }, [isResumeMode, adStatus]); // eslint-disable-line

  // adHasStarted kept as alias for backward-compat with validation logic below
  const adHasStarted = isStarted;

  // Wizard state
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(isResumeMode);
  const [adID, setAdID] = useState(null);

  // Step 1: Application + Placement Type
  const [appLoading, setAppLoading] = useState(false);
  const [integrationTypeLoading, setIntegrationTypeLoading] = useState(false);
  const [applications, setApplications] = useState([]);
  const [placementTypes, setPlacementTypes] = useState([]);
  const [selectedApplicationID, setSelectedApplicationID] = useState('');
  const [selectedPlacementTypeID, setSelectedPlacementTypeID] = useState('');
  const [resumePlacementType, setResumePlacementType] = useState('');
  const [passbackOptions, setPassbackOptions] = useState([]);
  const [selectedPassbackID, setSelectedPassbackID] = useState('');
  const [passbackLoading, setPassbackLoading] = useState(false);
  const [existingPlacements, setExistingPlacements] = useState([]);
  const [selectedPlacementUUID, setSelectedPlacementUUID] = useState('__new__');
  const [integrationType, setIntegrationType] = useState('gam');

  // Step 2: Basic Info
  const [adName, setAdName] = useState('');
  const [startTime, setStartTime] = useState('');
  const [endTime, setEndTime] = useState('');
  const [totalImpressions, setTotalImpressions] = useState('');
  const [dailyImpressions, setDailyImpressions] = useState('');
  const [enableDailyLimit, setEnableDailyLimit] = useState(false);

  // Step 3: Targeting (v2)
  const [enabledFeatures, setEnabledFeatures] = useState(null);
  const [hasCreative, setHasCreative] = useState(false);

  // Frequency capping
  const [freqTotal, setFreqTotal] = useState('');
  const [freqDaily, setFreqDaily] = useState('');

  // Day parts (grid-based)
  const [dayPartsActive, setDayPartsActive] = useState(false);
  const [dayPartsValue, setDayPartsValue] = useState({ ...EMPTY_DAY_PARTS });

  // Keywords (array of strings)
  const [includeKeywords, setIncludeKeywords] = useState([]);
  const [excludeKeywords, setExcludeKeywords] = useState([]);

  // Country & Language
  const [countryWhitelist, setCountryWhitelist] = useState([]);
  const [languageWhitelist, setLanguageWhitelist] = useState([]);

  // Demographic (AS-117 new schema: { all, selected })
  const [ageState, setAgeState] = useState(makeDefaultAgeGenderState());
  const [genderState, setGenderState] = useState(makeDefaultAgeGenderState());

  // Audience
  const [audienceWhitelist, setAudienceWhitelist] = useState([]);
  const [audienceBlacklist, setAudienceBlacklist] = useState([]);

  // Device / OS / Browser targeting (AS-116)
  const [deviceState, setDeviceState] = useState(makeDefaultState());
  const [osState, setOsState] = useState(makeDefaultState());
  const [browserState, setBrowserState] = useState(makeDefaultState());

  // Card-level enabled toggles
  const [freqEnabled, setFreqEnabled] = useState(false);
  const [keywordsEnabled, setKeywordsEnabled] = useState(false);
  const [regionEnabled, setRegionEnabled] = useState(false);
  const [demoEnabled, setDemoEnabled] = useState(false);
  const [audienceEnabled, setAudienceEnabled] = useState(false);

  // Leave confirmation
  const [showLeaveModal, setShowLeaveModal] = useState(false);
  const [isDirty, setIsDirty] = useState(false);

  // Pending navigation target when user tries to leave while isDirty
  const [pendingNav, setPendingNav] = useState(null);

  const companyID = sessionStorage.getItem('portal_company_id');

  // Mark dirty when any form field changes (after initial load)
  const markDirty = useCallback(() => setIsDirty(true), []);

  // Guarded navigate: intercepts React Router navigations when dirty
  const guardedNavigate = useCallback((path) => {
    if (isDirty) {
      setPendingNav(path);
      setShowLeaveModal(true);
    } else {
      navigate(path);
    }
  }, [isDirty, navigate]);

  // Fetch existing placements when application changes
  useEffect(() => {
    if (!selectedApplicationID) {
      setExistingPlacements([]);
      setSelectedPlacementUUID('__new__');
      return;
    }
    api.getApplicationPlacements(selectedApplicationID)
      .then((data) => {
        setExistingPlacements(data?.placements || []);
        // Hardcode: auto-select first existing; GAM: default to new
        if (integrationType === 'hardcode' && data?.placements?.length > 0) {
          setSelectedPlacementUUID(data.placements[0].uuid);
        } else {
          setSelectedPlacementUUID('__new__');
        }
      })
      .catch(() => setExistingPlacements([]));
  }, [selectedApplicationID, integrationType]);

  // Auto-select first filtered placement when placement type or placements change (hardcode mode)
  useEffect(() => {
    if (integrationType !== 'hardcode' || !selectedPlacementTypeID || existingPlacements.length === 0) return;
    const ptName = placementTypes.find(pt => String(pt.id) === String(selectedPlacementTypeID))?.name || '';
    const filtered = existingPlacements.filter(p => p.creativeType === ptName);
    if (filtered.length > 0) {
      setSelectedPlacementUUID(filtered[0].uuid);
    } else {
      setSelectedPlacementUUID('__new__');
    }
  }, [selectedPlacementTypeID, existingPlacements, integrationType, placementTypes]);

  // Fetch passbacks when placement type changes
  useEffect(() => {
    if (!selectedPlacementTypeID || !companyID) {
      setPassbackOptions([]);
      setSelectedPassbackID('');
      return;
    }
    const ptName = placementTypes.find(pt => String(pt.id) === String(selectedPlacementTypeID))?.name;
    if (!ptName) return;

    setPassbackLoading(true);
    api.getPassbacks(companyID)
      .then((data) => {
        const filtered = (data?.passbacks || []).filter(pb => pb.placement_type_id === ptName);
        setPassbackOptions(filtered);
        // Auto-select default
        const defaultPb = filtered.find(pb => pb.is_default);
        setSelectedPassbackID(defaultPb ? String(defaultPb.id) : '');
      })
      .catch(() => setPassbackOptions([]))
      .finally(() => setPassbackLoading(false));
  }, [selectedPlacementTypeID, companyID, placementTypes]);

  // Intercept browser close / refresh
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) { e.preventDefault(); e.returnValue = ''; }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // Cancel handler
  const handleCancel = () => {
    guardedNavigate('/ads');
  };

  const confirmLeave = () => {
    setIsDirty(false);
    setShowLeaveModal(false);
    navigate(pendingNav || '/ads');
    setPendingNav(null);
  };

  const cancelLeave = () => {
    setShowLeaveModal(false);
    setPendingNav(null);
  };

  // Helper: parse RFC3339 datetime to "YYYY-MM-DDTHH:mm" for form inputs
  const parseDateTime = (rfc3339) => {
    if (!rfc3339) return '';
    const d = new Date(rfc3339);
    const pad = (n) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
  };


  // Load draft data when in resume mode
  useEffect(() => {
    if (!isResumeMode || !companyID) return;

    const loadDraft = async () => {
      setInitialLoading(true);
      try {
        const ad = await api.getPortalAd(editId);

        // Set adID (PAC resources already created)
        setAdID(ad.id);
        setHasCreative((ad.creatives_count || 0) > 0);

        // Archived ads cannot edit dates — block early with a clear message
        if (ad.status === 'archived') {
          addToast('已封存的廣告無法編輯走期', 'error');
          navigate('/ads');
          return;
        }
        // NOTE: setAdStatus is deferred until AFTER initialStartTimeRef /
        // initialEndTimeRef are populated below — otherwise the isStarted
        // useMemo (deps [isResumeMode, adStatus]) recomputes against null
        // refs and caches false, breaking the lifecycle lock.

        // Step 1 data (read-only in resume) — fetch display name
        const types = await api.getPortalPlacementTypes();
        const matched = (types || []).find(t => t.name === ad.placement_type);
        setResumePlacementType(
          matched ? `${matched.display_name} (${matched.platform})` : (ad.placement_type || '')
        );

        // Step 2 data
        setAdName(ad.name || '');
        const parsedStart = parseDateTime(ad.start_time);
        const parsedEnd = parseDateTime(ad.end_time);
        setStartTime(parsedStart);
        setEndTime(parsedEnd);
        // Snapshot original times from DB — used for adHasStarted / isEndExpired.
        // Must be set before component re-renders — stored as refs, not state.
        initialStartTimeRef.current = parsedStart;
        initialEndTimeRef.current = parsedEnd;
        // Now safe: setAdStatus triggers the isStarted/isEnded useMemo
        // recompute, and the refs are already populated.
        setAdStatus(ad.status || '');
        setTotalImpressions(ad.total_impressions ? String(ad.total_impressions) : '');
        setDailyImpressions(ad.daily_impressions ? String(ad.daily_impressions) : '');
        setEnableDailyLimit(ad.daily_impressions > 0);

        // Step 3 data (targeting v2)
        if (ad.targeting) {
          const t = typeof ad.targeting === 'string' ? JSON.parse(ad.targeting) : ad.targeting;

          // Frequency capping
          if (t.frequency_capping_total || t.frequency_capping_daily) {
            setFreqEnabled(true);
            setFreqTotal(t.frequency_capping_total ? String(t.frequency_capping_total) : '');
            setFreqDaily(t.frequency_capping_daily ? String(t.frequency_capping_daily) : '');
          }

          // Day parts (v2 format: object with day keys)
          if (t.day_parts && typeof t.day_parts === 'object' && !Array.isArray(t.day_parts)) {
            setDayPartsActive(!!t.day_parts.isActive);
            const dpValue = { ...EMPTY_DAY_PARTS };
            for (const dayKey of Object.keys(EMPTY_DAY_PARTS)) {
              if (Array.isArray(t.day_parts[dayKey])) {
                dpValue[dayKey] = t.day_parts[dayKey];
              }
            }
            setDayPartsValue(dpValue);
          } else if (t.day_parts_switch !== undefined) {
            setDayPartsActive(!!t.day_parts_switch);
          }

          // Keywords
          if (t.keywords) {
            const inc = t.keywords.include || [];
            const exc = t.keywords.exclude || [];
            if (inc.length || exc.length) setKeywordsEnabled(true);
            setIncludeKeywords(inc);
            setExcludeKeywords(exc);
          } else if (t.include || t.exclude) {
            const inc = t.include || [];
            const exc = t.exclude || [];
            if (inc.length || exc.length) setKeywordsEnabled(true);
            setIncludeKeywords(inc);
            setExcludeKeywords(exc);
          }

          // Country & Language
          const hasCountry = t.country && !t.country.allow_all && (t.country.whitelist || []).length > 0;
          const hasLanguage = t.language && !t.language.allow_all && (t.language.whitelist || []).length > 0;
          if (hasCountry || hasLanguage) setRegionEnabled(true);
          if (t.country) setCountryWhitelist(t.country.whitelist || []);
          if (t.language) setLanguageWhitelist(t.language.whitelist || []);

          // Demographic (AS-117): read from stored targeting JSON
          // The stored JSON (in saas_ads.targeting) may use old shape { gender: { allow_all, whitelist } }
          // or new shape { gender_state: { all, selected } } depending on when it was saved.
          // We always try new shape first, then fall back to old shape for backward compat.
          let loadedAgeState = null;
          let loadedGenderState = null;
          if (t.age_state) {
            loadedAgeState = t.age_state;
          } else if (t.age) {
            // old shape: { allow_all, whitelist }
            const ageAll = t.age.allow_all !== false || (t.age.whitelist || []).length === 0;
            loadedAgeState = { all: ageAll, selected: t.age.whitelist || [] };
          }
          if (t.gender_state) {
            loadedGenderState = t.gender_state;
          } else if (t.gender) {
            const genderAll = t.gender.allow_all !== false || (t.gender.whitelist || []).length === 0;
            loadedGenderState = { all: genderAll, selected: t.gender.whitelist || [] };
          }
          if (loadedAgeState) setAgeState(loadedAgeState);
          if (loadedGenderState) setGenderState(loadedGenderState);
          const hasGender = loadedGenderState && !loadedGenderState.all;
          const hasAge = loadedAgeState && !loadedAgeState.all;
          if (hasGender || hasAge) setDemoEnabled(true);

          // Audience
          const hasAudience = (t.audience?.whitelist || []).length > 0 || (t.audience?.blacklist || []).length > 0;
          if (hasAudience) setAudienceEnabled(true);
          if (t.audience) {
            setAudienceWhitelist(t.audience.whitelist || []);
            setAudienceBlacklist(t.audience.blacklist || []);
          }

          // Device / OS / Browser (AS-116) — read from stored targeting fields
          // The backend saves the UpdateTargetingRequest JSON which includes device/mobile_os/browser objects.
          // Parse using the same logic as AdGroup v3/v2 fallback.
          const ts2Str = t.targetingSettings_v2;
          const ts2 = ts2Str ? (typeof ts2Str === 'string' ? (() => { try { return JSON.parse(ts2Str); } catch { return {}; } })() : ts2Str) : {};
          setDeviceState(parseDimensionFromAdGroup(
            { device_all: t.device?.all, device_mode: t.device?.mode, device: t.device?.selected },
            ts2, 'device'
          ));
          setOsState(parseDimensionFromAdGroup(
            { mobile_os_all: t.mobile_os?.all, mobile_os_mode: t.mobile_os?.mode, mobile_os: t.mobile_os?.selected },
            ts2, 'os'
          ));
          setBrowserState(parseDimensionFromAdGroup(
            { browser_all: t.browser?.all, browser_mode: t.browser?.mode, browser: t.browser?.selected },
            ts2, 'browser'
          ));
        }

        // Load features for Step 3
        const featuresData = await api.getPortalFeatures();
        setEnabledFeatures(featuresData.features?.targeting || {});

        // Jump to the target step
        const targetStep = parseInt(resumeStep) || ad.current_step || 2;
        setCurrentStep(targetStep);
      } catch (err) {
        addToast('載入草稿失敗：' + err.message, 'error');
        navigate('/ads');
      } finally {
        setInitialLoading(false);
      }
    };

    loadDraft();
  }, [editId]); // eslint-disable-line

  // Fetch data for Step 1 (only in new ad mode)
  useEffect(() => {
    if (!companyID) {
      navigate('/login');
      return;
    }
    if (!isResumeMode && currentStep === 1) {
      fetchApplications();
      fetchPlacementTypes();
    }
  }, [currentStep, companyID, navigate]); // eslint-disable-line

  const fetchApplications = async () => {
    setAppLoading(true);
    try {
      const data = await api.getPortalApplications(companyID);
      setApplications(data || []);
      // Auto-select only when there is exactly one application
      if (data && data.length === 1) {
        setSelectedApplicationID(data[0].uuid);
        // Fetch integration type for the single auto-selected app
        fetchIntegrationTypeForApp(data[0].uuid);
      }
      // When multiple apps: do NOT auto-select; user must choose explicitly
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setAppLoading(false);
    }
  };

  const fetchIntegrationTypeForApp = async (appUuid) => {
    if (!appUuid) return;
    setIntegrationTypeLoading(true);
    try {
      const result = await api.getApplicationIntegrationType(appUuid);
      if (result?.integration_type) setIntegrationType(result.integration_type);
    } catch (err) {
      console.warn('Failed to fetch integration type for app', appUuid, err);
      setIntegrationType(null);
      addToast('無法取得整合類型：' + err.message, 'error');
    } finally {
      setIntegrationTypeLoading(false);
    }
  };

  const fetchPlacementTypes = async () => {
    try {
      const data = await api.getPortalPlacementTypes();
      setPlacementTypes(data || []);
      if (data && data.length > 0 && !selectedPlacementTypeID) {
        setSelectedPlacementTypeID(String(data[0].id));
      }
    } catch (err) {
      addToast(err.message, 'error');
    }
  };

  const handleStep1Next = () => {
    if (!selectedApplicationID || !selectedPlacementTypeID) {
      addToast('請選擇 Application 和 Placement Type', 'error');
      return;
    }
    if (integrationType === 'hardcode') {
      const ptName = placementTypes.find(pt => String(pt.id) === String(selectedPlacementTypeID))?.name || '';
      const filtered = existingPlacements.filter(p => p.creativeType === ptName);
      if (filtered.length === 0) {
        addToast('此版位類型下無既有 Placement，請聯絡系統管理員建立後再使用', 'error');
        return;
      }
      if (!selectedPlacementUUID || selectedPlacementUUID === '__new__') {
        addToast('請選擇版位', 'error');
        return;
      }
    }
    if (!startTime) {
      const today = new Date();
      const todayStr = today.toISOString().split('T')[0];
      setStartTime(`${todayStr}T00:00`);
    }
    if (!endTime) {
      const tomorrow = new Date();
      tomorrow.setDate(tomorrow.getDate() + 1);
      const tomorrowStr = tomorrow.toISOString().split('T')[0];
      setEndTime(`${tomorrowStr}T23:59`);
    }
    setCurrentStep(2);
  };

  const handleStep2Next = async () => {
    if (!adName || !startTime || !endTime || !totalImpressions) {
      addToast('請填寫所有必填欄位', 'error');
      return;
    }

    // Date-level validation (compare YYYY-MM-DD only, no time component)
    const startDateStr = getDatePart(startTime);
    const endDateStr = getDatePart(endTime);

    // For new ads and unstarted resume: start must be >= today
    if (!adHasStarted && startDateStr < todayStr) {
      addToast('開始時間不能早於今天', 'error');
      return;
    }
    // end must always be >= today
    if (endDateStr < todayStr) {
      addToast('結束時間不能早於今天', 'error');
      return;
    }
    // end must be after start (date-level)
    if (endDateStr < startDateStr) {
      addToast('結束時間必須晚於開始時間', 'error');
      return;
    }

    setLoading(true);
    try {
      if (isResumeMode && adID) {
        // Resume mode: update existing ad via PUT
        await api.updatePortalAd(adID, {
          ad_name: adName,
          start_time: startTime,
          end_time: endTime,
          total_impressions: parseInt(totalImpressions),
          daily_impressions: dailyImpressions ? parseInt(dailyImpressions) : 0,
        });
        // Load features for Step 3 if not already loaded
        if (!enabledFeatures) {
          const featuresData = await api.getPortalFeatures();
          setEnabledFeatures(featuresData.features?.targeting || {});
        }
      } else {
        // New ad: Create the ad (campaign + placement + order + adgroup)
        // Get selected passback code
        const selectedPb = passbackOptions.find(pb => String(pb.id) === selectedPassbackID);
        const passbackCode = selectedPb?.code || '';

        const result = await api.createPortalAd({
          application_id: selectedApplicationID,
          placement_type_id: parseInt(selectedPlacementTypeID),
          company_id: companyID,
          ad_name: adName,
          start_time: startTime,
          end_time: endTime,
          total_impressions: parseInt(totalImpressions),
          daily_impressions: dailyImpressions ? parseInt(dailyImpressions) : 0,
          passback_code: passbackCode,
          placement_uuid: integrationType === 'hardcode' && selectedPlacementUUID !== '__new__' ? selectedPlacementUUID : '',
        });
        setAdID(result.ad_id);

        // Fetch account features for Step 3
        const featuresData = await api.getPortalFeatures();
        setEnabledFeatures(featuresData.features?.targeting || {});
      }

      setCurrentStep(3);
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleStep3Save = async () => {
    // Validate device/os/browser (AS-116): cannot submit with all=false and empty selection
    const deviceOsError = validateDeviceOsStates(deviceState, osState, browserState);
    if (deviceOsError) {
      addToast(deviceOsError, 'error');
      return;
    }
    // Validate age/gender (AS-117): if demoEnabled, cannot submit with all=false and empty selection
    if (demoEnabled) {
      const ageGenderError = validateAgeGenderStates(ageState, genderState);
      if (ageGenderError) {
        addToast(ageGenderError, 'error');
        return;
      }
    }

    setLoading(true);
    try {
      const payload = {
        frequency_capping_total: freqEnabled && freqTotal ? parseInt(freqTotal) : 0,
        frequency_capping_daily: freqEnabled && freqDaily ? parseInt(freqDaily) : 0,
        day_parts: {
          isActive: dayPartsActive,
          mon: dayPartsValue.mon || [],
          tue: dayPartsValue.tue || [],
          wed: dayPartsValue.wed || [],
          thu: dayPartsValue.thu || [],
          fri: dayPartsValue.fri || [],
          sat: dayPartsValue.sat || [],
          sun: dayPartsValue.sun || [],
        },
        keywords: {
          include: keywordsEnabled ? includeKeywords : [],
          exclude: keywordsEnabled ? excludeKeywords : [],
        },
        language: {
          allow_all: !regionEnabled,
          whitelist: regionEnabled ? languageWhitelist : [],
        },
        country: {
          allow_all: !regionEnabled,
          whitelist: regionEnabled ? countryWhitelist : [],
        },
        // AS-117 new schema: age_state / gender_state (all + selected, no mode)
        age_state: {
          all:      !demoEnabled || ageState.all,
          selected: demoEnabled ? (ageState.selected || []) : [],
        },
        gender_state: {
          all:      !demoEnabled || genderState.all,
          selected: demoEnabled ? (genderState.selected || []) : [],
        },
        audience: {
          whitelist: audienceEnabled ? audienceWhitelist : [],
          blacklist: audienceEnabled ? audienceBlacklist : [],
        },
        // Device / OS / Browser (AS-116)
        device:    buildDimensionPayload(deviceState),
        mobile_os: buildDimensionPayload(osState),
        browser:   buildDimensionPayload(browserState),
      };

      await api.updatePortalAdTargeting(adID, payload);

      setIsDirty(false);
      addToast(isResumeMode ? '廣告設定已儲存' : '廣告建立成功！', 'success');
      if (isResumeMode && hasCreative) {
        navigate(`/ads/${adID}`);
      } else {
        setCurrentStep(4);
      }
    } catch (err) {
      addToast(err.message, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSkipTargeting = () => {
    setIsDirty(false);
    addToast(isResumeMode ? '廣告設定已儲存' : '廣告建立成功！', 'success');
    if (isResumeMode && hasCreative) {
      navigate(`/ads/${adID}`);
    } else {
      setCurrentStep(4);
    }
  };


  if (initialLoading) {
    return (
      <div className="create-ad-wizard">
        <div className="wizard-header">
          <h1>載入草稿中...</h1>
        </div>
        <div className="wizard-content">
          <div className="loading">載入中...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="create-ad-wizard">
      <div className="wizard-header">
        <h1>{isResumeMode ? '繼續編輯廣告' : '建立廣告'}</h1>
        <div className="wizard-steps-nav">
          <WizardSteps
            steps={[
              { key: 'placement', label: '選擇版位' },
              { key: 'basic',     label: '基本資訊' },
              { key: 'targeting', label: 'Targeting' },
            ]}
            currentIndex={currentStep - 1}
            onStepClick={(idx) => {
              if (idx + 1 < currentStep || isResumeMode) setCurrentStep(idx + 1);
            }}
          />
        </div>
      </div>

      <div className="wizard-content">
        {currentStep === 1 && !isResumeMode && (
          <div className="step-1">
            <h2>選擇 Application 和版位類型</h2>
            <p className="step-description">
              選擇後將無法更改，請確認後再進入下一步
            </p>

            {appLoading ? (
              <div className="loading"><Spinner /></div>
            ) : applications.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: 'var(--text-secondary)' }}>
                此公司尚未設定 Application，請聯繫管理員
              </div>
            ) : (
              <>
                {applications.length > 1 && (
                  <div className="form-group">
                    <label>Application（媒體/網站）*</label>
                    <select
                      value={selectedApplicationID}
                      onChange={(e) => {
                        const uuid = e.target.value;
                        setSelectedApplicationID(uuid);
                        markDirty();
                        if (uuid) fetchIntegrationTypeForApp(uuid);
                      }}
                      required
                    >
                      <option value="">請選擇 Application</option>
                      {applications.map((app) => (
                        <option key={app.uuid} value={app.uuid}>
                          {app.app_name} ({app.os_type})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                <div className="form-group">
                  <label>Placement Type（版位類型）</label>
                  {integrationTypeLoading ? (
                    <div style={{ padding: '8px 0' }}><Spinner size="sm" /></div>
                  ) : (
                  <select
                    value={selectedPlacementTypeID}
                    onChange={(e) => { setSelectedPlacementTypeID(e.target.value); setSelectedPlacementUUID('__new__'); markDirty(); }}
                    required
                    disabled={!selectedApplicationID}
                  >
                    <option value="">請選擇 Placement Type</option>
                    {placementTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.display_name} ({type.platform})
                      </option>
                    ))}
                  </select>
                  )}
                </div>

                {integrationType === 'hardcode' && selectedApplicationID && selectedPlacementTypeID && (() => {
                  const ptName = placementTypes.find(pt => String(pt.id) === String(selectedPlacementTypeID))?.name || '';
                  const filtered = existingPlacements.filter(p => p.creativeType === ptName);
                  if (filtered.length === 0) {
                    return (
                      <div className="form-group">
                        <label>選擇版位</label>
                        <p className="form-hint" style={{ color: 'var(--text-secondary, #888)' }}>
                          此版位類型下無既有 Placement，請聯絡系統管理員建立後再使用
                        </p>
                      </div>
                    );
                  }
                  return (
                    <div className="form-group">
                      <label>選擇版位</label>
                      <select
                        value={selectedPlacementUUID}
                        onChange={(e) => { setSelectedPlacementUUID(e.target.value); markDirty(); }}
                      >
                        {filtered.map((p) => (
                          <option key={p.uuid} value={p.uuid}>
                            {p.placement_name}
                          </option>
                        ))}
                      </select>
                    </div>
                  );
                })()}

                {selectedPlacementTypeID && passbackOptions.length > 1 && (
                  <div className="form-group">
                    <label>Passback 設定</label>
                    <select
                      value={selectedPassbackID}
                      onChange={(e) => { setSelectedPassbackID(e.target.value); markDirty(); }}
                    >
                      {passbackOptions.map((pb) => (
                        <option key={pb.id} value={pb.id}>
                          {pb.name}{pb.is_default ? '（預設）' : ''}
                        </option>
                      ))}
                      <option value="">不使用 Passback</option>
                    </select>
                  </div>
                )}
              </>
            )}

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">

                <Button onClick={handleStep1Next} disabled={appLoading || applications.length === 0}>
                  下一步
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 1 && isResumeMode && (
          <div className="step-1">
            <h2>版位資訊（已鎖定）</h2>
            <p className="step-description">
              版位已選定，無法更改
            </p>

            <div className="form-group">
              <label>Placement Type（版位類型）</label>
              <div className="readonly-value">{resumePlacementType || '-'}</div>
            </div>

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">
                <Button onClick={() => setCurrentStep(2)}>
                  下一步
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 2 && (
          <div className="step-2" onChange={markDirty}>
            <h2>基本資訊</h2>

            <div className="form-group">
              <label>廣告名稱 *</label>
              <input
                type="text"
                value={adName}
                onChange={(e) => setAdName(e.target.value)}
                placeholder="輸入廣告名稱"
                required
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  走期開始 *
                  {/* start_time is locked when: already started, already ended, or archived */}
                  {(isStarted || isEnded || isArchived) && (
                    <span className="field-tooltip" title={
                      isArchived ? '廣告已封存，不可編輯' :
                      isEnded    ? '廣告已結束' :
                      '已開始投放後不可變更開始時間'
                    }> ⓘ</span>
                  )}
                </label>
                <div className="datetime-inputs">
                  <input
                    type="date"
                    value={getDatePart(startTime)}
                    onChange={(e) => setStartTime(setDatePart(startTime, e.target.value))}
                    min={(isStarted || isEnded || isArchived) ? undefined : todayStr}
                    readOnly={isStarted || isEnded || isArchived}
                    disabled={isStarted || isEnded || isArchived}
                    required
                  />
                  <input
                    type="text"
                    className="time-input"
                    value={getTimePart(startTime)}
                    onChange={(e) => setStartTime(setTimePart(startTime, e.target.value))}
                    placeholder="00:00"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength={5}
                    readOnly={isStarted || isEnded || isArchived}
                    disabled={isStarted || isEnded || isArchived}
                    required
                  />
                </div>
              </div>
              <div className="form-group">
                <label>
                  走期結束 *
                  {/* end_time is locked when: expired (>3 days past end) or archived */}
                  {(isEndExpired || isArchived) && (
                    <span className="field-tooltip" title="廣告已結束逾 3 天，不可再編輯"> ⓘ</span>
                  )}
                </label>
                <div className="datetime-inputs">
                  <input
                    type="date"
                    value={getDatePart(endTime)}
                    onChange={(e) => setEndTime(setDatePart(endTime, e.target.value, '23:59'))}
                    min={(isEndExpired || isArchived) ? undefined : todayStr}
                    readOnly={isEndExpired || isArchived}
                    disabled={isEndExpired || isArchived}
                    required
                  />
                  <input
                    type="text"
                    className="time-input"
                    value={getTimePart(endTime, '23:59')}
                    onChange={(e) => setEndTime(setTimePart(endTime, e.target.value))}
                    placeholder="23:59"
                    pattern="[0-2][0-9]:[0-5][0-9]"
                    maxLength={5}
                    readOnly={isEndExpired || isArchived}
                    disabled={isEndExpired || isArchived}
                    required
                  />
                </div>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>
                  總曝光數 *
                  {/* total_impressions is locked when expired or archived */}
                  {(isEndExpired || isArchived) && (
                    <span className="field-tooltip" title="廣告已結束逾 3 天，不可再編輯"> ⓘ</span>
                  )}
                </label>
                <input
                  type="text"
                  value={formatNumber(totalImpressions)}
                  onChange={handleNumericInput(setTotalImpressions)}
                  placeholder="10,000"
                  disabled={isEndExpired || isArchived}
                  required
                />
              </div>
              {integrationType !== 'gam' && (
              <div className="form-group">
                <label className="toggle-label" style={{ marginBottom: 8 }}>
                  <input
                    type="checkbox"
                    checked={enableDailyLimit}
                    disabled={isEndExpired || isArchived}
                    onChange={(e) => {
                      setEnableDailyLimit(e.target.checked);
                      if (!e.target.checked) setDailyImpressions('');
                    }}
                  />
                  {/* daily_impressions is locked when expired or archived */}
                  <span>
                    設定日曝光上限
                    {(isEndExpired || isArchived) && (
                      <span className="field-tooltip" title="廣告已結束逾 3 天，不可再編輯"> ⓘ</span>
                    )}
                  </span>
                </label>
                <input
                  type="text"
                  value={formatNumber(dailyImpressions)}
                  onChange={handleNumericInput(setDailyImpressions)}
                  placeholder="1,000"
                  disabled={!enableDailyLimit || isEndExpired || isArchived}
                />
              </div>
              )}
            </div>

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">

                <Button variant="secondary" onClick={() => setCurrentStep(1)}>
                  上一步
                </Button>
                <Button onClick={handleStep2Next} disabled={loading}>
                  {loading ? '處理中...' : '下一步'}
                </Button>
              </div>
            </div>
          </div>
        )}

        {currentStep === 3 && enabledFeatures && (
          <div className="step-3" onChange={markDirty} onClick={markDirty}>
            <TargetingForm
              enabledFeatures={enabledFeatures}
              freqEnabled={freqEnabled} onFreqEnabledChange={setFreqEnabled}
              freqTotal={freqTotal} onFreqTotalChange={setFreqTotal}
              freqDaily={freqDaily} onFreqDailyChange={setFreqDaily}
              keywordsEnabled={keywordsEnabled} onKeywordsEnabledChange={setKeywordsEnabled}
              includeKeywords={includeKeywords} onIncludeKeywordsChange={setIncludeKeywords}
              excludeKeywords={excludeKeywords} onExcludeKeywordsChange={setExcludeKeywords}
              regionEnabled={regionEnabled} onRegionEnabledChange={setRegionEnabled}
              countryWhitelist={countryWhitelist} onCountryWhitelistChange={setCountryWhitelist}
              languageWhitelist={languageWhitelist} onLanguageWhitelistChange={setLanguageWhitelist}
              demoEnabled={demoEnabled} onDemoEnabledChange={setDemoEnabled}
              ageState={ageState} onAgeStateChange={setAgeState}
              genderState={genderState} onGenderStateChange={setGenderState}
              dayPartsActive={dayPartsActive} onDayPartsActiveChange={setDayPartsActive}
              dayPartsValue={dayPartsValue} onDayPartsValueChange={setDayPartsValue}
              audienceEnabled={audienceEnabled} onAudienceEnabledChange={setAudienceEnabled}
              audienceWhitelist={audienceWhitelist} onAudienceWhitelistChange={setAudienceWhitelist}
              audienceBlacklist={audienceBlacklist} onAudienceBlacklistChange={setAudienceBlacklist}
              deviceState={deviceState} onDeviceStateChange={setDeviceState}
              osState={osState} onOsStateChange={setOsState}
              browserState={browserState} onBrowserStateChange={setBrowserState}
            />

            <div className="wizard-actions">
              <Button variant="secondary" onClick={handleCancel}>取消</Button>
              <div className="wizard-actions-right">

                <Button variant="secondary" onClick={() => setCurrentStep(2)}>上一步</Button>
                <Button onClick={handleStep3Save} disabled={loading}>
                  {loading ? '儲存中...' : isResumeMode ? '儲存變更' : '完成建立'}
                </Button>
              </div>
            </div>
          </div>
        )}
        {currentStep === 4 && (
          <div className="step-complete">
            <div className="step-complete__icon">
              <Icon name="circle-check" size={64} color="var(--status-success-border)" />
            </div>
            <h2 className="step-complete__title">廣告建立成功！</h2>
            <p className="step-complete__desc">
              廣告已建立完成。建議您立即上傳廣告素材，以便廣告可以順利投放。
            </p>
            <div className="step-complete__actions">
              <Button onClick={() => navigate(`/ads/${adID}?tab=creatives`)}>
                建立廣告素材
              </Button>
              <button
                className="step-complete__later-link"
                onClick={() => navigate('/ads')}
              >
                稍後再建立
              </button>
            </div>
          </div>
        )}
      </div>

      {showLeaveModal && (
        <Modal
          title="離開建立廣告"
          onClose={cancelLeave}
          footer={
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={cancelLeave}>繼續編輯</Button>
              <Button onClick={confirmLeave}>確定離開</Button>
            </div>
          }
        >
          <p>有未儲存的變更，確定要離開？</p>
        </Modal>
      )}
    </div>
  );
}
